import { useContext, useState } from "react";
import ToastMsg from "../../components/common/ToastMsg";
import { SETTINGS_CHANGE_PASSWORD } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import { Eye, EyeClosed, CheckCircle2, XCircle, AlertTriangle, LogOut, Key, Shield, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { cn } from "../../utils/tailwindUtils";

/* ----------------------------- theme primitives ---------------------------- */

const SettingCard = ({ icon: Icon, title, subtitle, action, children, className }) => (
    <div className={cn("bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5", className)}>
        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                <Icon className="h-4.5 w-4.5" />
            </span>
            <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
            </div>
            {action}
        </div>
        {children}
    </div>
);

const PasswordInput = ({ value, onChange, placeholder, label }) => {
    const [showPassword, setShowPassword] = useState(false);
    return (
        <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5">{label}</label>
            <div className="relative">
                <input
                    autoComplete="new-password"
                    type={showPassword ? "text" : "password"}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className="w-full pr-10 px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary focus:border-sidebar-primary transition"
                />
                <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-sidebar-primary transition-colors"
                >
                    {showPassword ? <Eye size={18} /> : <EyeClosed size={18} />}
                </button>
            </div>
        </div>
    );
};

const PasswordRequirement = ({ met, text }) => (
    <div className="flex items-center gap-2.5">
        {met ? (
            <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
        ) : (
            <XCircle className="h-4 w-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
        )}
        <span className={cn("text-sm", met ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-500")}>
            {text}
        </span>
    </div>
);

/* --------------------------------- screen ---------------------------------- */

const Security = () => {
    let navigate = useNavigate();
    const { logoutauth } = useContext(AuthContext);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const { create } = useApiMethods();

    const passwordPolicy = sessionStorage.getItem("password_policy");
    const parsedPolicy = passwordPolicy ? JSON.parse(passwordPolicy) : null;

    const validatePassword = (password) => {
        if (!parsedPolicy) return { isValid: true, requirements: [] };
        const requirements = [
            { met: password.length >= parsedPolicy.min_length, text: `Minimum ${parsedPolicy.min_length} characters` },
            { met: password.length <= parsedPolicy.max_length, text: `Maximum ${parsedPolicy.max_length} characters` },
            { met: (password.match(/\d/g) || []).length >= parsedPolicy.min_number, text: `At least ${parsedPolicy.min_number} number(s)` },
            { met: (password.match(/[a-z]/g) || []).length >= parsedPolicy.min_lower, text: `At least ${parsedPolicy.min_lower} lowercase letter(s)` },
            { met: (password.match(/[A-Z]/g) || []).length >= parsedPolicy.min_upper, text: `At least ${parsedPolicy.min_upper} uppercase letter(s)` },
            { met: (password.match(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g) || []).length >= parsedPolicy.min_chars, text: `At least ${parsedPolicy.min_chars} special character(s)` },
        ];
        return { isValid: requirements.every((req) => req.met), requirements };
    };

    const { isValid: isNewPasswordValid, requirements: newPasswordRequirements } = validatePassword(newPassword);

    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            ToastMsg("error", "Please fill in all fields");
            return;
        }
        if (newPassword !== confirmPassword) {
            ToastMsg("error", "New password and confirm password do not match");
            return;
        }
        if (parsedPolicy && !isNewPasswordValid) {
            ToastMsg("error", "New password does not meet the requirements");
            return;
        }

        setLoading(true);
        try {
            const payload = { current_password: currentPassword, new_password: newPassword };
            const response = await create(SETTINGS_CHANGE_PASSWORD, payload);
            if (response.status === "success") {
                ToastMsg("success", response.message);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setShowLogoutConfirm(true);
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoutAllDevices = () => {
        ToastMsg("success", "Logged out from all devices");
        setShowLogoutConfirm(false);
        logoutauth();
        navigate("/");
    };

    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                    <Shield className="h-5 w-5" />
                </div>
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Security & Access</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Manage your password and account security settings.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* Change Password */}
                <SettingCard icon={Key} title="Change Password" subtitle="Update your password to keep your account secure.">
                    <div className="space-y-4">
                        <PasswordInput
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter your current password"
                            label="Current Password"
                        />
                        <PasswordInput
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Create a new password"
                            label="New Password"
                        />

                        {parsedPolicy && newPassword && (
                            <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 p-3.5">
                                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2.5 flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-sidebar-primary" />
                                    Password Requirements
                                </h3>
                                <div className="space-y-2">
                                    {newPasswordRequirements.map((requirement, index) => (
                                        <PasswordRequirement key={index} met={requirement.met} text={requirement.text} />
                                    ))}
                                </div>
                            </div>
                        )}

                        <PasswordInput
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm your new password"
                            label="Confirm New Password"
                        />

                        {newPassword && confirmPassword && (
                            <div className={cn(
                                "flex items-center gap-2.5 p-2.5 rounded-lg border text-sm font-medium",
                                passwordsMatch
                                    ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800 text-green-700 dark:text-green-300"
                                    : "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 text-red-700 dark:text-red-300"
                            )}>
                                {passwordsMatch ? <CheckCircle2 className="h-4.5 w-4.5 flex-shrink-0" /> : <XCircle className="h-4.5 w-4.5 flex-shrink-0" />}
                                <span>{passwordsMatch ? "Passwords match" : "Passwords do not match"}</span>
                            </div>
                        )}

                        <Button
                            onClick={handleChangePassword}
                            disabled={loading || !isNewPasswordValid || newPassword !== confirmPassword}
                            className="w-full bg-sidebar hover:bg-sidebar-primary text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {loading ? (
                                <>
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Changing Password...
                                </>
                            ) : (
                                <>
                                    <Key className="h-4 w-4" />
                                    Update Password
                                </>
                            )}
                        </Button>
                    </div>
                </SettingCard>

                {/* Right column */}
                <div className="space-y-6">
                    {/* Security Status */}
                    <SettingCard
                        icon={Shield}
                        title="Security Status"
                        subtitle="Overview of your account protection."
                        action={
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Protected
                            </span>
                        }
                    >
                        <div className="space-y-3">
                            {/* Password expiry */}
                            <div className="flex items-center justify-between rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3.5 py-3">
                                <div className="flex items-center gap-3">
                                    <span className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 grid place-items-center">
                                        <Clock className="h-4.5 w-4.5" />
                                    </span>
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Password Expiry</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Days until reset required</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                        {parsedPolicy?.expairy_period || 0}
                                    </div>
                                    <div className="text-xs text-gray-400">days left</div>
                                </div>
                            </div>

                            {/* Session security */}
                            <div className="flex items-start gap-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3.5 py-3">
                                <span className="w-9 h-9 rounded-lg bg-sidebar/10 text-sidebar-primary grid place-items-center flex-shrink-0">
                                    <Shield className="h-4.5 w-4.5" />
                                </span>
                                <div className="min-w-0">
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Session Security</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                                        {parsedPolicy?.session_time_out ? (
                                            <span className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                                Auto-logout after{" "}
                                                <span className="font-semibold text-sidebar-primary">
                                                    {parsedPolicy.time_out_value || 30} minutes
                                                </span>{" "}
                                                of inactivity
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-gray-400 rounded-full" />
                                                Session timeout disabled
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </SettingCard>

                    {/* Danger Zone */}
                    <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-900/10 shadow-sm p-5">
                        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-red-200/60 dark:border-red-800/60">
                            <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 grid place-items-center">
                                <AlertTriangle className="h-4.5 w-4.5" />
                            </span>
                            <div>
                                <h2 className="text-base font-semibold text-red-800 dark:text-red-300">Danger Zone</h2>
                                <p className="text-xs text-red-700/80 dark:text-red-400/80 mt-0.5">Critical actions affecting your account.</p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 p-3.5 bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-800">
                            <div className="min-w-0">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Logout All Devices</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Sign out from all active sessions.</p>
                            </div>
                            <Button
                                onClick={() => setShowLogoutConfirm(true)}
                                className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0 flex items-center gap-1.5"
                            >
                                <LogOut className="h-4 w-4" />
                                Logout All
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 grid place-items-center">
                                <AlertTriangle className="h-5 w-5" />
                            </span>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Logout All Devices?</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                            This will immediately sign you out from all active sessions across all devices. You'll need to log in again on each device.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => setShowLogoutConfirm(false)}
                                className="border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 px-6"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleLogoutAllDevices}
                                className="bg-red-600 hover:bg-red-700 text-white px-6 flex items-center gap-1.5"
                            >
                                <LogOut className="h-4 w-4" />
                                Confirm Logout
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Security;
